import { useState } from "react";
import { REFBRIEF, bi, setBi } from "@/lib/i18n";
import { api, downloadReferenceBriefPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, CircleDot, ClipboardCopy, FileSignature, Download } from "lucide-react";

export const ReferenceBrief = ({ cvId, cv, lang, onChange }) => {
  const r = REFBRIEF[lang];
  const st = cv.statement || {};
  const value = bi(st.referenceBrief, lang);
  const [busy, setBusy] = useState(false);

  const setBrief = (v) => onChange({ ...st, referenceBrief: setBi(st.referenceBrief, lang, v) });

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/cvs/${cvId}/reference-brief`, { language: lang });
      onChange({ ...st, referenceBrief: setBi(st.referenceBrief, lang, data.brief || ""), referenceBriefAt: data.generatedAt });
    } catch (e) {
      toast.error(e.response?.data?.detail || r.error);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(value); toast.success(r.copied); }
    catch { toast.error("Error"); }
  };

  const downloadPdf = async () => {
    try { await downloadReferenceBriefPdf(cvId, lang, `referans_brifingi_${lang}.pdf`); }
    catch (e) { toast.error(e.response?.data?.detail || r.error); }
  };

  return (
    <div data-testid="reference-brief" className="rounded-xl border border-[#1a2e4a]/20 bg-white p-4 space-y-3">
      <div className="flex items-center gap-2 text-[15px] font-bold text-[#1a2e4a]">
        <FileSignature className="h-4 w-4 text-[#c0392b]" /> {r.title}
      </div>
      <p className="text-[12.5px] text-[#64748b] leading-relaxed">{r.intro}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button data-testid="reference-brief-generate" disabled={busy} onClick={generate} className="bg-[#c0392b] hover:bg-[#a93226] text-white h-10">
          {busy ? <CircleDot className="h-4 w-4 mr-2 animate-pulse" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {busy ? r.generating : value ? r.regenerate : r.generate}
        </Button>
        {value && (
          <Button data-testid="reference-brief-copy" variant="outline" onClick={copy} className="border-[#1a2e4a] text-[#1a2e4a] h-10">
            <ClipboardCopy className="h-4 w-4 mr-2" /> {r.copy}
          </Button>
        )}
        {value && (
          <Button data-testid="reference-brief-pdf" variant="outline" onClick={downloadPdf} className="border-[#c0392b] text-[#c0392b] h-10">
            <Download className="h-4 w-4 mr-2" /> {r.downloadPdf}
          </Button>
        )}
      </div>
      <div>
        <Label className="text-[13px] text-[#1a2e4a]">{r.label}</Label>
        <Textarea data-testid="reference-brief-text" value={value} onChange={(e) => setBrief(e.target.value)} rows={12} placeholder={r.placeholder} className="mt-1 leading-relaxed" />
      </div>
    </div>
  );
};
